import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Bell, Plus, Edit, Trash2, Mail, Users, User, UserCog, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_LABELS = {
  ticket_created: 'New Ticket Created',
  ticket_updated: 'Ticket Updated',
  ticket_status_changed: 'Status Changed',
  ticket_priority_changed: 'Priority Changed',
  ticket_assigned: 'Ticket Assigned',
  comment_added: 'Comment Added',
  internal_note_added: 'Internal Note Added',
  attachment_added: 'Attachment Added',
  escalation_requested: 'Escalation Requested',
  escalation_required: 'Escalation Required',
  ticket_resolved: 'Ticket Resolved',
  ticket_closed: 'Ticket Closed'
};

const RECIPIENT_TYPE_LABELS = {
  role: 'Role',
  user: 'Specific User',
  assignee: 'Ticket Assignee',
  creator: 'Ticket Creator',
  customer: 'Customer Contact'
};

export default function NotificationSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    event: 'ticket_created',
    recipient_type: 'role',
    recipient_value: 'admin',
    send_email: true,
    send_in_app: false,
    description: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notification-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    try {
      const response = await api.post('/notification-settings/create-defaults');
      toast.success(response.data.message);
      loadSettings();
    } catch (error) {
      toast.error('Failed to create default settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/notification-settings/${editingId}`, formData);
        toast.success('Notification setting updated');
      } else {
        await api.post('/notification-settings', formData);
        toast.success('Notification setting created');
      }
      
      setDialogOpen(false);
      resetForm();
      loadSettings();
    } catch (error) {
      toast.error('Failed to save notification setting');
    }
  };

  const handleEdit = (setting) => {
    setEditingId(setting.id);
    setFormData({
      event: setting.event,
      recipient_type: setting.recipient_type,
      recipient_value: setting.recipient_value,
      send_email: setting.send_email,
      send_in_app: setting.send_in_app,
      description: setting.description || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (settingId) => {
    if (!window.confirm('Delete this notification setting?')) return;
    
    try {
      await api.delete(`/notification-settings/${settingId}`);
      toast.success('Notification setting deleted');
      loadSettings();
    } catch (error) {
      toast.error('Failed to delete notification setting');
    }
  };

  const handleToggleEnabled = async (settingId, currentEnabled) => {
    try {
      await api.put(`/notification-settings/${settingId}`, { enabled: !currentEnabled });
      loadSettings();
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      event: 'ticket_created',
      recipient_type: 'role',
      recipient_value: 'admin',
      send_email: true,
      send_in_app: false,
      description: ''
    });
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.event]) {
      acc[setting.event] = [];
    }
    acc[setting.event].push(setting);
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Settings
            </CardTitle>
            <CardDescription>
              Configure who receives email notifications for ticket events
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {settings.length === 0 && (
              <Button variant="outline" onClick={handleCreateDefaults}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Create Defaults
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Notification Rule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Edit' : 'Create'} Notification Rule</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Event Type *</Label>
                    <Select
                      value={formData.event}
                      onValueChange={(value) => setFormData({...formData, event: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(EVENT_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Recipient Type *</Label>
                    <Select
                      value={formData.recipient_type}
                      onValueChange={(value) => setFormData({...formData, recipient_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="role">By Role</SelectItem>
                        <SelectItem value="assignee">Ticket Assignee</SelectItem>
                        <SelectItem value="customer">Customer Contact</SelectItem>
                        <SelectItem value="creator">Ticket Creator</SelectItem>
                        <SelectItem value="user">Specific User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Recipient *</Label>
                    {formData.recipient_type === 'role' ? (
                      <Select
                        value={formData.recipient_value}
                        onValueChange={(value) => setFormData({...formData, recipient_value: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">All Admins</SelectItem>
                          <SelectItem value="technician">All Technicians</SelectItem>
                          <SelectItem value="customer">All Customers</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : formData.recipient_type === 'user' ? (
                      <Input
                        type="email"
                        value={formData.recipient_value}
                        onChange={(e) => setFormData({...formData, recipient_value: e.target.value})}
                        placeholder="user@example.com"
                      />
                    ) : (
                      <Input
                        value={formData.recipient_type}
                        disabled
                        className="bg-gray-50"
                      />
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Optional description"
                    />
                  </div>
                  
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.send_email}
                        onCheckedChange={(checked) => setFormData({...formData, send_email: checked})}
                      />
                      <Label className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Send Email Notification
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.send_in_app}
                        onCheckedChange={(checked) => setFormData({...formData, send_in_app: checked})}
                      />
                      <Label className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Send In-App Notification
                      </Label>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingId ? 'Update' : 'Create'} Rule
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">Loading notification settings...</div>
        ) : settings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Bell className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-semibold mb-2">No Notification Rules</p>
            <p className="text-sm mb-4">Create default notification rules to get started</p>
            <Button onClick={handleCreateDefaults}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Create Default Notifications
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedSettings).map(([event, eventSettings]) => (
              <div key={event} className="space-y-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {EVENT_LABELS[event] || event}
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>In-App</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventSettings.map((setting) => (
                      <TableRow key={setting.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {setting.recipient_type === 'role' && <Users className="h-4 w-4 text-gray-500" />}
                            {setting.recipient_type === 'user' && <User className="h-4 w-4 text-gray-500" />}
                            {setting.recipient_type === 'assignee' && <UserCog className="h-4 w-4 text-gray-500" />}
                            <div>
                              <p className="font-semibold capitalize">{RECIPIENT_TYPE_LABELS[setting.recipient_type]}</p>
                              <p className="text-xs text-gray-500">{setting.recipient_value}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {setting.send_email ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {setting.send_in_app ? (
                            <CheckCircle className="h-4 w-4 text-blue-600" />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={setting.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {setting.enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleEnabled(setting.id, setting.enabled)}
                            >
                              <Switch checked={setting.enabled} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(setting)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(setting.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
